/* Amares Homes — scroll-scrubbed frame sequence.
 *
 * Draws a preloaded image sequence to a canvas, stepping through it as the
 * visitor scrolls a tall pinned section. This is the approach Apple uses for
 * product pages: seeking a <video> with currentTime is unreliable, especially
 * on iOS Safari, whereas decoded images always land on the exact frame.
 *
 * Degrades on purpose:
 *   - narrow screens fall back to the poster (decoding ~120 images on a phone
 *     is not worth the battery)
 *   - prefers-reduced-motion shows a single frame
 *   - frames still loading draw the nearest one that is ready
 */
(function () {
  'use strict';

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  var CONCURRENCY = 6;

  function ScrollVideo(root) {
    this.root = root;
    this.canvas = root.querySelector('[data-amx-canvas]');
    this.poster = root.querySelector('[data-amx-poster]');
    this.progressEl = root.querySelector('[data-amx-loadbar]');
    this.minWidth = parseInt(root.dataset.amxMinWidth, 10) || 750;

    var payload = root.querySelector('[data-amx-frames]');
    this.urls = payload ? JSON.parse(payload.textContent) : [];
    this.count = this.urls.length;

    if (!this.canvas || !this.count) return;

    this.ctx = this.canvas.getContext('2d', { alpha: false });
    this.images = new Array(this.count);
    this.ready = new Array(this.count).fill(false);
    this.loadedCount = 0;
    this.current = -1;

    // The drawn frame chases the scroll position rather than tracking it
    // exactly, so a flick of the wheel glides through frames instead of
    // jumping. 1 = no damping, lower = heavier.
    var smoothing = parseFloat(root.dataset.amxSmoothing);
    this.lerp = isNaN(smoothing) ? 0.18 : Math.max(0.02, Math.min(1, 1 - smoothing / 100));
    this.target = 0;
    this.rendered = 0;
    this.settled = true;
    this.lastTime = 0;

    this.onScroll = this.onScroll.bind(this);
    this.onResize = this.onResize.bind(this);
    this.request = this.request.bind(this);

    this.evaluate();
    reduceMotion.addEventListener('change', this.evaluate.bind(this));
    window.addEventListener('resize', this.onResize, { passive: true });
  }

  /** Decide whether to run at all, based on viewport and motion preference. */
  ScrollVideo.prototype.evaluate = function () {
    var active = window.innerWidth >= this.minWidth && !reduceMotion.matches;

    if (active === this.active) return;
    this.active = active;
    this.root.classList.toggle('amx-scrollvideo--static', !active);

    if (!active) {
      window.removeEventListener('scroll', this.request);
      return;
    }

    this.resizeCanvas();
    this.load();
    window.addEventListener('scroll', this.request, { passive: true });
    this.onScroll();
  };

  /* ------------------------------------------------------------- loading -- */

  ScrollVideo.prototype.load = function () {
    if (this.loadStarted) return;
    this.loadStarted = true;

    var self = this;
    var next = 0;

    // First frame first, so something is on screen immediately.
    this.loadOne(0, function () {
      self.draw(0);
      if (self.poster) self.poster.classList.add('amx-scrollvideo__poster--hidden');
    });

    function pump() {
      while (next < self.count) {
        var index = next++;
        if (index === 0) continue;
        self.loadOne(index, pump);
        return;
      }
    }

    for (var i = 0; i < CONCURRENCY; i++) pump();
  };

  ScrollVideo.prototype.loadOne = function (index, done) {
    if (this.images[index]) return done && done();

    var self = this;
    var img = new Image();
    img.decoding = 'async';
    this.images[index] = img;

    img.onload = function () {
      var finish = function () {
        self.ready[index] = true;
        self.loadedCount++;
        self.updateProgress();
        // Redraw if this frame is at or next to where we're currently sitting.
        if (index === self.current || index === self.current + 1) {
          self.drawFrom(self.rendered);
        }
        if (done) done();
      };

      // Decode up front so the first drawImage of each frame doesn't stall the
      // compositor mid-scrub. Older browsers just skip straight to finish.
      if (typeof img.decode === 'function') {
        img.decode().then(finish, finish);
      } else {
        finish();
      }
    };
    img.onerror = function () {
      self.loadedCount++;
      self.updateProgress();
      if (done) done();
    };

    img.src = this.urls[index];
  };

  ScrollVideo.prototype.updateProgress = function () {
    if (!this.progressEl) return;
    var pct = (this.loadedCount / this.count) * 100;
    this.progressEl.style.width = pct.toFixed(1) + '%';
    if (this.loadedCount >= this.count) {
      this.progressEl.parentNode.classList.add('amx-scrollvideo__loading--done');
    }
  };

  /* ------------------------------------------------------------ painting -- */

  ScrollVideo.prototype.resizeCanvas = function () {
    var rect = this.canvas.getBoundingClientRect();
    if (!rect.width || !rect.height) return;

    var dpr = Math.min(window.devicePixelRatio || 1, 2);
    var w = Math.round(rect.width * dpr);
    var h = Math.round(rect.height * dpr);
    if (this.canvas.width === w && this.canvas.height === h) return;

    this.canvas.width = w;
    this.canvas.height = h;
    var img = this.nearestReady(this.current < 0 ? 0 : this.current);
    if (img) this.paint(img);
  };

  /** Falls back to the closest loaded frame so scrubbing never blanks out. */
  ScrollVideo.prototype.nearestReady = function (index) {
    if (this.ready[index]) return this.images[index];
    for (var d = 1; d < this.count; d++) {
      if (index - d >= 0 && this.ready[index - d]) return this.images[index - d];
      if (index + d < this.count && this.ready[index + d]) return this.images[index + d];
    }
    return null;
  };

  ScrollVideo.prototype.paint = function (img, alpha) {
    if (!img || !img.naturalWidth) return;

    var cw = this.canvas.width;
    var ch = this.canvas.height;
    // object-fit: cover, done by hand.
    var scale = Math.max(cw / img.naturalWidth, ch / img.naturalHeight);
    var w = img.naturalWidth * scale;
    var h = img.naturalHeight * scale;

    if (alpha != null && alpha < 1) this.ctx.globalAlpha = alpha;
    this.ctx.drawImage(img, (cw - w) / 2, (ch - h) / 2, w, h);
    this.ctx.globalAlpha = 1;
  };

  ScrollVideo.prototype.draw = function (index) {
    this.current = index;
    var img = this.nearestReady(index);
    if (img) this.paint(img, 1);
  };

  /* ------------------------------------------------------------ scrolling -- */

  ScrollVideo.prototype.scrollProgress = function () {
    var top = 0;
    var node = this.root;
    while (node) {
      top += node.offsetTop;
      node = node.offsetParent;
    }
    var span = Math.max(1, this.root.offsetHeight - window.innerHeight);
    return Math.max(0, Math.min(1, (window.scrollY - top) / span));
  };

  /** Scroll handler: move the target, wake the damping loop. */
  ScrollVideo.prototype.request = function () {
    if (!this.active) return;
    this.target = this.scrollProgress() * (this.count - 1);
    if (this.settled) {
      this.settled = false;
      this.lastTime = 0;
      var self = this;
      window.requestAnimationFrame(function (t) {
        self.tick(t);
      });
    }
  };

  ScrollVideo.prototype.tick = function (now) {
    if (!this.active) {
      this.settled = true;
      return;
    }

    var dt = this.lastTime ? Math.min(now - this.lastTime, 100) : 16.67;
    this.lastTime = now;

    // Frame-rate corrected, so high-refresh displays don't scrub faster.
    var factor = 1 - Math.pow(1 - this.lerp, dt / 16.67);
    var delta = this.target - this.rendered;

    if (Math.abs(delta) < 0.01) {
      this.rendered = this.target;
      this.drawFrom(this.rendered);
      this.settled = true;
      return;
    }

    this.rendered += delta * factor;
    this.drawFrom(this.rendered);

    var self = this;
    window.requestAnimationFrame(function (t) {
      self.tick(t);
    });
  };

  /**
   * Blends the two frames either side of the (fractional) position instead of
   * snapping to the nearest one. The sequence is sampled well below source
   * frame rate, so without this you see discrete steps however smooth the
   * scroll easing is — the jitter is in the frames, not the timing.
   */
  ScrollVideo.prototype.drawFrom = function (value) {
    var clamped = Math.max(0, Math.min(this.count - 1, value));
    var index = Math.floor(clamped);
    var frac = clamped - index;

    var base = this.nearestReady(index);
    if (!base) return;

    this.current = index;
    this.paint(base, 1);

    // Fade the next frame in over the top as we travel toward it.
    var next = index + 1;
    if (frac > 0.01 && next < this.count && this.ready[next]) {
      this.paint(this.images[next], frac);
    }
  };

  /** Snap without easing — init and resize. */
  ScrollVideo.prototype.onScroll = function () {
    if (!this.active) return;
    this.target = this.scrollProgress() * (this.count - 1);
    this.rendered = this.target;
    this.drawFrom(this.rendered);
  };

  ScrollVideo.prototype.onResize = function () {
    this.evaluate();
    if (this.active) {
      this.resizeCanvas();
      this.onScroll();
    }
  };

  /* ----------------------------------------------------------------- boot -- */

  function boot(scope) {
    var roots = (scope || document).querySelectorAll('[data-amx-scrollvideo]');
    Array.prototype.forEach.call(roots, function (root) {
      if (root.dataset.amxBound) return;
      root.dataset.amxBound = '1';
      new ScrollVideo(root);
    });
  }

  if (document.readyState !== 'loading') boot();
  else document.addEventListener('DOMContentLoaded', function () { boot(); });

  document.addEventListener('shopify:section:load', function (e) { boot(e.target); });
})();
